import { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Button,
  Stack,
  Typography,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Tooltip,
  Card,
  Grid,
  Tabs,
  Tab,
  useTheme,
  alpha,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';

import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import ScienceIcon from '@mui/icons-material/Science';
import SchoolIcon from '@mui/icons-material/School';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import LanguageIcon from '@mui/icons-material/Language';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import DescriptionIcon from '@mui/icons-material/Description';
import GavelIcon from '@mui/icons-material/Gavel';
import SettingsIcon from '@mui/icons-material/Settings';
import LayersIcon from '@mui/icons-material/Layers';

import { useApiContext } from '@/contexts/apiContext';
import { useUserContext } from '@/contexts/userContext';
import DataGridTable from '@/components/datagridTable';
import DrawerPanel from '@/components/drawerPanel';
import ProgressIndicator from '@/components/progressIndicator';
import { JsonViewer } from '@/components/viewers';

export interface ProgramDefSettingItem {
  name: string;
  type: string;
  label: string;
  required?: boolean;
  defaultValue?: any;
  valueOptions?: string[];
}

export interface ProgramDefSettingSection {
  sectionName: string;
  items: ProgramDefSettingItem[];
}

export interface ProgramLifecycleStage {
  key: string;
  label: string;
  order: number;
}

export interface ProgramDocumentType {
  key: string;
  label: string;
  basis?: string;
  stage?: number;
}

export interface ProgramObligationType {
  key: string;
  label: string;
  basis?: string;
  clockStart?: string;
  durationDays?: number | null;
}

export interface ProgramPipelinePurpose {
  key: string;
  label: string;
}

export interface ProgramDefItem {
  id: string;
  tenantId: string;
  domainId?: string;
  name: string;
  label: string;
  description?: string;
  settingDefs?: ProgramDefSettingSection[];
  lifecycleStages?: ProgramLifecycleStage[];
  documentTypes?: ProgramDocumentType[];
  obligationTypes?: ProgramObligationType[];
  pipelinePurposes?: ProgramPipelinePurpose[];
  instanceIcon?: string;
  active: boolean;
  metadata?: Record<string, any>;
  createdDate?: string | Date;
  createdBy?: string;
  lastUpdatedDate?: string | Date;
  lastUpdatedBy?: string;
}

interface Props {
  maxHeight?: number;
}

const getDomainIcon = (domainId?: string, size = 18) => {
  switch (domainId?.toLowerCase()) {
    case 'biopharm':
      return <ScienceIcon sx={{ fontSize: size, color: '#10B981' }} />;
    case 'education':
      return <SchoolIcon sx={{ fontSize: size, color: '#8B5CF6' }} />;
    case 'itsupport':
      return <SupportAgentIcon sx={{ fontSize: size, color: '#0EA5E9' }} />;
    default:
      return <LanguageIcon sx={{ fontSize: size, color: '#6366F1' }} />;
  }
};

const ProgramManagement = ({ maxHeight }: Props) => {
  const apiContext = useApiContext();
  const { user } = useUserContext();
  const { t } = useTranslation();
  const theme = useTheme();

  const [programs, setPrograms] = useState<ProgramDefItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [domainFilter, setDomainFilter] = useState<string>('all');

  // Drawer state
  const [selectedProgram, setSelectedProgram] = useState<ProgramDefItem | null>(null);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [drawerTab, setDrawerTab] = useState<number>(0);
  const [copiedJson, setCopiedJson] = useState<boolean>(false);

  // Create / Edit modal state
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [editingProgram, setEditingProgram] = useState<ProgramDefItem | null>(null);
  const [formData, setFormData] = useState<Partial<ProgramDefItem>>({});

  const loadPrograms = async () => {
    try {
      setLoading(true);
      // Fetch only REAL program definitions from program_def table
      const rawPrograms = await apiContext.getProgramDefs().catch((err) => {
        console.error('Failed to load program_defs from database:', err);
        return [];
      });

      if (Array.isArray(rawPrograms)) {
        const mapped: ProgramDefItem[] = rawPrograms.map((p: any) => ({
          id: p.id,
          tenantId: p.tenantId || user?.tenantId || 'genericTenant',
          domainId: p.domainId || 'biopharm',
          name: p.name || p.label || p.id,
          label: p.label || p.name || p.id,
          description: p.description || '',
          settingDefs: Array.isArray(p.settingDefs) ? p.settingDefs : [],
          lifecycleStages: Array.isArray(p.lifecycleStages) ? p.lifecycleStages : [],
          documentTypes: Array.isArray(p.documentTypes) ? p.documentTypes : [],
          obligationTypes: Array.isArray(p.obligationTypes) ? p.obligationTypes : [],
          pipelinePurposes: Array.isArray(p.pipelinePurposes) ? p.pipelinePurposes : [],
          instanceIcon: p.instanceIcon || 'clinical',
          active: p.active !== false,
          metadata: p.metadata || {},
          createdDate: p.createdDate,
          createdBy: p.createdBy,
          lastUpdatedDate: p.lastUpdatedDate,
          lastUpdatedBy: p.lastUpdatedBy,
        }));
        setPrograms(mapped);
      } else {
        setPrograms([]);
      }
    } catch {
      setPrograms([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPrograms();
  }, [user?.tenantId]);

  const handleOpenDrawer = (program: ProgramDefItem, initialTab = 0) => {
    setSelectedProgram(program);
    setDrawerTab(initialTab);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
  };

  const copyProgramJson = () => {
    if (!selectedProgram) return;
    navigator.clipboard.writeText(JSON.stringify(selectedProgram, null, 2));
    setCopiedJson(true);
    setTimeout(() => setCopiedJson(false), 2000);
  };

  const handleOpenCreateDialog = () => {
    setEditingProgram(null);
    setFormData({
      id: '',
      name: '',
      label: '',
      domainId: 'biopharm',
      description: '',
      instanceIcon: 'clinical',
      active: true,
      settingDefs: [],
      lifecycleStages: [],
      documentTypes: [],
      obligationTypes: [],
      pipelinePurposes: [],
      metadata: {},
    });
    setDialogOpen(true);
  };

  const handleOpenEditDialog = (program: ProgramDefItem) => {
    setEditingProgram(program);
    setFormData({ ...program });
    setDialogOpen(true);
  };

  const handleSaveProgram = async () => {
    if (!formData.name?.trim() && !formData.label?.trim()) return;

    try {
      const targetId = editingProgram
        ? editingProgram.id
        : (formData.id?.trim() || (formData.name ? formData.name.toLowerCase().replace(/[^a-z0-9]/g, '') : `prog_${Date.now()}`));

      const payload: any = {
        id: targetId,
        name: formData.name || formData.label,
        label: formData.label || formData.name,
        domainId: formData.domainId || 'biopharm',
        description: formData.description || '',
        instanceIcon: formData.instanceIcon || 'clinical',
        active: formData.active !== false,
        settingDefs: formData.settingDefs || [],
        lifecycleStages: formData.lifecycleStages || [],
        documentTypes: formData.documentTypes || [],
        obligationTypes: formData.obligationTypes || [],
        pipelinePurposes: formData.pipelinePurposes || [],
        metadata: formData.metadata || {},
      };

      await apiContext.saveProgramDef(payload);
      await loadPrograms();

      if (selectedProgram && selectedProgram.id === payload.id) {
        setSelectedProgram({
          ...selectedProgram,
          ...payload,
        });
      }
    } catch (err) {
      console.error('Failed to save program definition:', err);
    }
    setDialogOpen(false);
  };

  const handleDeleteProgram = async (programId: string) => {
    if (window.confirm(`Are you sure you want to deactivate program definition "${programId}"?`)) {
      try {
        await apiContext.deleteProgramDef(programId);
        await loadPrograms();
        if (selectedProgram?.id === programId) {
          setDrawerOpen(false);
        }
      } catch (err) {
        console.error('Failed to delete program definition:', err);
      }
    }
  };

  // Filtered real program definitions
  const filteredPrograms = useMemo(() => {
    return programs.filter((p) => {
      const q = searchQuery.toLowerCase().trim();
      const matchesSearch =
        !q ||
        p.id.toLowerCase().includes(q) ||
        p.name.toLowerCase().includes(q) ||
        p.label.toLowerCase().includes(q) ||
        (p.description && p.description.toLowerCase().includes(q));

      const matchesDomain = domainFilter === 'all' || p.domainId?.toLowerCase() === domainFilter.toLowerCase();
      return matchesSearch && matchesDomain;
    });
  }, [programs, searchQuery, domainFilter]);

  // Real KPI Metrics derived strictly from the database rows
  const totalStages = useMemo(
    () => programs.reduce((acc, p) => acc + (p.lifecycleStages?.length || 0), 0),
    [programs],
  );
  const totalDocTypes = useMemo(
    () => programs.reduce((acc, p) => acc + (p.documentTypes?.length || 0), 0),
    [programs],
  );
  const totalObligations = useMemo(
    () => programs.reduce((acc, p) => acc + (p.obligationTypes?.length || 0), 0),
    [programs],
  );
  const activeCount = useMemo(
    () => programs.filter((p) => p.active !== false).length,
    [programs],
  );

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: t('programManagement.columns.id', 'Program Def ID'),
      width: 170,
      renderCell: (params) => (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ height: '100%' }}>
          <BusinessCenterIcon sx={{ fontSize: 16, color: 'secondary.main' }} />
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'monospace',
              fontWeight: 800,
              fontSize: '0.8rem',
              color: 'text.primary',
            }}
          >
            {params.value}
          </Typography>
        </Stack>
      ),
    },
    {
      field: 'name',
      headerName: t('programManagement.columns.name', 'Program Definition Name'),
      flex: 1.3,
      minWidth: 230,
      renderCell: (params) => (
        <Box sx={{ overflow: 'hidden' }}>
          <Typography variant="body2" sx={{ fontWeight: 750, color: 'text.primary' }}>
            {params.value}
          </Typography>
          {params.row.description && (
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontSize: '0.725rem',
                display: '-webkit-box',
                WebkitLineClamp: 1,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {params.row.description}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'domainId',
      headerName: t('programManagement.columns.domain', 'Domain'),
      width: 130,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ height: '100%' }}>
          {getDomainIcon(params.value, 16)}
          <Typography variant="body2" sx={{ textTransform: 'capitalize', fontWeight: 600, fontSize: '0.775rem' }}>
            {params.value || 'biopharm'}
          </Typography>
        </Stack>
      ),
    },
    {
      field: 'lifecycleStages',
      headerName: t('programManagement.columns.lifecycleStages', 'Lifecycle Stages'),
      width: 150,
      renderCell: (params) => {
        const stages: ProgramLifecycleStage[] = params.value || [];
        const tooltip = stages.map((s) => `${s.order}. ${s.label}`).join(' → ');
        return (
          <Tooltip title={tooltip || 'No lifecycle stages defined'}>
            <Chip
              size="small"
              icon={<LayersIcon style={{ fontSize: 13 }} />}
              label={`${stages.length} Stages`}
              onClick={() => handleOpenDrawer(params.row, 1)}
              sx={{
                fontWeight: 700,
                fontSize: '0.725rem',
                cursor: 'pointer',
                bgcolor: alpha(theme.palette.secondary.main, 0.08),
                color: 'secondary.main',
                border: '1px solid',
                borderColor: alpha(theme.palette.secondary.main, 0.25),
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      field: 'documentTypes',
      headerName: t('programManagement.columns.documentTypes', 'Controlled Docs'),
      width: 150,
      renderCell: (params) => {
        const docs: ProgramDocumentType[] = params.value || [];
        const tooltip = docs.map((d) => d.label).join(', ');
        return (
          <Tooltip title={tooltip || 'No document types registered'}>
            <Chip
              size="small"
              icon={<DescriptionIcon style={{ fontSize: 13 }} />}
              label={`${docs.length} Doc Types`}
              onClick={() => handleOpenDrawer(params.row, 2)}
              sx={{
                fontWeight: 700,
                fontSize: '0.725rem',
                cursor: 'pointer',
                bgcolor: 'rgba(14, 165, 233, 0.08)',
                color: '#0284C7',
                border: '1px solid rgba(14, 165, 233, 0.25)',
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      field: 'obligationTypes',
      headerName: t('programManagement.columns.obligations', 'Obligations'),
      width: 140,
      renderCell: (params) => {
        const obs: ProgramObligationType[] = params.value || [];
        const tooltip = obs.map((o) => o.label).join(', ');
        return (
          <Tooltip title={tooltip || 'No regulatory obligations'}>
            <Chip
              size="small"
              icon={<GavelIcon style={{ fontSize: 13 }} />}
              label={`${obs.length} Obligations`}
              onClick={() => handleOpenDrawer(params.row, 3)}
              sx={{
                fontWeight: 700,
                fontSize: '0.725rem',
                cursor: 'pointer',
                bgcolor: 'rgba(245, 158, 11, 0.08)',
                color: '#D97706',
                border: '1px solid rgba(245, 158, 11, 0.25)',
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      field: 'pipelinePurposes',
      headerName: t('programManagement.columns.purposes', 'Purposes'),
      width: 130,
      renderCell: (params) => {
        const purposes: ProgramPipelinePurpose[] = params.value || [];
        return (
          <Chip
            size="small"
            icon={<AccountTreeIcon style={{ fontSize: 13 }} />}
            label={`${purposes.length} Purposes`}
            onClick={() => handleOpenDrawer(params.row, 4)}
            sx={{
              fontWeight: 700,
              fontSize: '0.725rem',
              cursor: 'pointer',
              bgcolor: 'rgba(99, 102, 241, 0.08)',
              color: '#6366F1',
              border: '1px solid rgba(99, 102, 241, 0.25)',
            }}
          />
        );
      },
    },
    {
      field: 'active',
      headerName: t('programManagement.columns.status', 'Status'),
      width: 105,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.value ? 'Active' : 'Inactive'}
          sx={{
            fontWeight: 700,
            fontSize: '0.725rem',
            bgcolor: params.value ? 'rgba(16, 185, 129, 0.1)' : 'rgba(100, 116, 139, 0.1)',
            color: params.value ? '#059669' : '#64748B',
            border: `1px solid ${params.value ? 'rgba(16, 185, 129, 0.25)' : 'rgba(100, 116, 139, 0.25)'}`,
          }}
        />
      ),
    },
    {
      field: 'actions',
      headerName: t('programManagement.columns.actions', 'Actions'),
      headerAlign: 'left',
      align: 'left',
      width: 125,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          justifyContent="flex-start"
          sx={{ width: '100%', height: '100%' }}
        >
          <Tooltip title="View Definition Details">
            <IconButton
              size="small"
              onClick={() => handleOpenDrawer(params.row, 0)}
              sx={{
                p: '3px',
                color: 'secondary.main',
                '&:hover': { bgcolor: alpha(theme.palette.secondary.main, 0.1) },
              }}
            >
              <VisibilityIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Edit Program Definition">
            <IconButton
              size="small"
              onClick={() => handleOpenEditDialog(params.row)}
              sx={{
                p: '3px',
                color: 'text.secondary',
                '&:hover': { bgcolor: 'action.hover', color: 'primary.main' },
              }}
            >
              <EditIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Deactivate">
            <IconButton
              size="small"
              onClick={() => handleDeleteProgram(params.row.id)}
              sx={{
                p: '3px',
                color: 'text.secondary',
                '&:hover': { bgcolor: 'error.main', color: 'common.white' },
              }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
      {/* Header and Controls */}
      <Box
        sx={{
          p: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 1.5,
          bgcolor: 'background.paper',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <BusinessCenterIcon color="secondary" sx={{ fontSize: 22 }} />
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
              {t('programManagement.title', 'Program Definitions (program_def)')}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {t('programManagement.subtitle', 'Live definitions registered in public.program_def for governance & lifecycle management')}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" sx={{ width: { xs: '100%', sm: 'auto' } }}>
          {/* Domain Filter */}
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <Select
              value={domainFilter}
              onChange={(e) => setDomainFilter(e.target.value)}
              sx={{ height: 34, fontSize: '0.775rem', fontWeight: 600 }}
            >
              <MenuItem value="all">All Domains</MenuItem>
              <MenuItem value="biopharm">BioPharm</MenuItem>
              <MenuItem value="general">General</MenuItem>
              <MenuItem value="itsupport">IT Support</MenuItem>
              <MenuItem value="education">Education</MenuItem>
            </Select>
          </FormControl>

          {/* Search Field */}
          <TextField
            size="small"
            placeholder={t('programManagement.search', 'Search program defs...')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              minWidth: { xs: '100%', sm: 220 },
              '& .MuiInputBase-input': { fontSize: '0.775rem', py: 0.75 },
            }}
          />

          <Tooltip title="Reload from program_def table">
            <IconButton size="small" onClick={loadPrograms} sx={{ border: 1, borderColor: 'divider', height: 34, width: 34 }}>
              <RefreshIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>

          <Button
            size="small"
            variant="contained"
            color="secondary"
            onClick={handleOpenCreateDialog}
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '8px',
              fontSize: '0.775rem',
              height: 34,
              whiteSpace: 'nowrap',
            }}
          >
            {t('programManagement.actions.createProgram', 'New Definition')}
          </Button>
        </Stack>
      </Box>

      {/* Real KPI Summary Cards */}
      <Box sx={{ p: 1.5, pb: 0.5 }}>
        <Grid container spacing={1.5}>
          <Grid size={{ xs: 6, sm: 2.4 }}>
            <Card variant="outlined" sx={{ p: 1.25, borderRadius: '10px', bgcolor: alpha(theme.palette.secondary.main, 0.03) }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>
                Program Definitions
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, mt: 0.25 }}>
                {programs.length}
              </Typography>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 2.4 }}>
            <Card variant="outlined" sx={{ p: 1.25, borderRadius: '10px', bgcolor: 'rgba(16, 185, 129, 0.03)' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>
                Active Definitions
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#059669', mt: 0.25 }}>
                {activeCount}
              </Typography>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 2.4 }}>
            <Card variant="outlined" sx={{ p: 1.25, borderRadius: '10px', bgcolor: 'rgba(99, 102, 241, 0.03)' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>
                Lifecycle Stages
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#6366F1', mt: 0.25 }}>
                {totalStages} Stages
              </Typography>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 2.4 }}>
            <Card variant="outlined" sx={{ p: 1.25, borderRadius: '10px', bgcolor: 'rgba(14, 165, 233, 0.03)' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>
                Controlled Documents
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#0284C7', mt: 0.25 }}>
                {totalDocTypes} Types
              </Typography>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 2.4 }}>
            <Card variant="outlined" sx={{ p: 1.25, borderRadius: '10px', bgcolor: 'rgba(245, 158, 11, 0.03)' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>
                Compliance Obligations
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#D97706', mt: 0.25 }}>
                {totalObligations} Clocks
              </Typography>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Main DataGrid Table */}
      <Box sx={{ flex: 1, overflow: 'hidden', p: 1, display: 'flex', flexDirection: 'column' }}>
        {loading ? (
          <ProgressIndicator />
        ) : (
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <DataGridTable
              rows={filteredPrograms}
              columns={columns}
              loading={loading}
              pageSize={10}
              height={maxHeight ? `${maxHeight - 145}px` : '100%'}
            />
          </Box>
        )}
      </Box>

      {/* Program Definition Details Drawer */}
      <DrawerPanel
        open={drawerOpen}
        onClose={handleCloseDrawer}
        minWidth="55vw"
        title={selectedProgram?.name || 'Program Definition Details'}
        subTitle={`ID: ${selectedProgram?.id || ''} | Domain: ${selectedProgram?.domainId || 'biopharm'}`}
      >
        {selectedProgram && (
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%', gap: 2 }}>
            <Tabs
              value={drawerTab}
              onChange={(_, val) => setDrawerTab(val)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label="Overview" icon={<BusinessCenterIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
              <Tab label={`Lifecycle Stages (${selectedProgram.lifecycleStages?.length || 0})`} icon={<LayersIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
              <Tab label={`Document Types (${selectedProgram.documentTypes?.length || 0})`} icon={<DescriptionIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
              <Tab label={`Obligations (${selectedProgram.obligationTypes?.length || 0})`} icon={<GavelIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
              <Tab label={`Purposes (${selectedProgram.pipelinePurposes?.length || 0})`} icon={<AccountTreeIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
              <Tab label={`Setting Defs (${selectedProgram.settingDefs?.length || 0})`} icon={<SettingsIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
              <Tab label="JSON Schema" icon={<ContentCopyIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
            </Tabs>

            {/* Tab 0: Overview */}
            {drawerTab === 0 && (
              <Stack spacing={2}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: '8px' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.secondary', mb: 1 }}>
                    Description
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.6, color: 'text.primary' }}>
                    {selectedProgram.description || 'No description provided.'}
                  </Typography>
                </Paper>

                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: '8px' }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                        Domain Workspace
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                        {getDomainIcon(selectedProgram.domainId, 18)}
                        <Typography variant="body2" sx={{ fontWeight: 700, textTransform: 'capitalize' }}>
                          {selectedProgram.domainId}
                        </Typography>
                      </Stack>
                    </Paper>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: '8px' }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                        Instance Icon
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.5, fontFamily: 'monospace' }}>
                        {selectedProgram.instanceIcon || 'clinical'}
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: '8px' }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                        Status
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          size="small"
                          label={selectedProgram.active ? 'Active' : 'Inactive'}
                          sx={{
                            fontWeight: 700,
                            bgcolor: selectedProgram.active ? 'rgba(16, 185, 129, 0.1)' : 'rgba(100, 116, 139, 0.1)',
                            color: selectedProgram.active ? '#059669' : '#64748B',
                          }}
                        />
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: '8px' }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                        Created By & Date
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                        {selectedProgram.createdBy || 'system'} ({selectedProgram.createdDate ? new Date(selectedProgram.createdDate).toLocaleDateString() : 'N/A'})
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>

                {selectedProgram.metadata && Object.keys(selectedProgram.metadata).length > 0 && (
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: '8px' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.secondary', mb: 1 }}>
                      Definition Metadata & Flags
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {Object.entries(selectedProgram.metadata).map(([key, value]) => (
                        <Chip
                          key={key}
                          size="small"
                          label={`${key}: ${typeof value === 'object' ? JSON.stringify(value) : value}`}
                          variant="outlined"
                          sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                        />
                      ))}
                    </Stack>
                  </Paper>
                )}
              </Stack>
            )}

            {/* Tab 1: Lifecycle Stages */}
            {drawerTab === 1 && (
              <Stack spacing={1.5}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Sequential stage progression configured for programs instantiated from this definition.
                </Typography>
                {selectedProgram.lifecycleStages && selectedProgram.lifecycleStages.length > 0 ? (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 800, width: 80 }}>Order</TableCell>
                        <TableCell sx={{ fontWeight: 800, width: 140 }}>Stage Key</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>Stage Display Label</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedProgram.lifecycleStages.map((stage) => (
                        <TableRow key={stage.key} hover>
                          <TableCell>
                            <Chip size="small" label={`#${stage.order}`} sx={{ fontWeight: 800, fontSize: '0.725rem' }} />
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.775rem' }}>
                            {stage.key}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{stage.label}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <Typography variant="body2" sx={{ color: 'text.secondary', py: 2 }}>
                    No lifecycle stages defined for this program definition.
                  </Typography>
                )}
              </Stack>
            )}

            {/* Tab 2: Document Types */}
            {drawerTab === 2 && (
              <Stack spacing={1.5}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Controlled regulatory and scientific document types governed under this program.
                </Typography>
                {selectedProgram.documentTypes && selectedProgram.documentTypes.length > 0 ? (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 800, width: 140 }}>Document Key</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>Document Name</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>Regulatory Basis</TableCell>
                        <TableCell sx={{ fontWeight: 800, width: 80 }}>Stage</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedProgram.documentTypes.map((doc) => (
                        <TableRow key={doc.key} hover>
                          <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.775rem' }}>
                            {doc.key}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{doc.label}</TableCell>
                          <TableCell sx={{ color: 'text.secondary', fontSize: '0.775rem' }}>
                            {doc.basis || '—'}
                          </TableCell>
                          <TableCell>
                            {doc.stage ? (
                              <Chip size="small" label={`Stage ${doc.stage}`} sx={{ fontWeight: 700, fontSize: '0.7rem' }} />
                            ) : (
                              '—'
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <Typography variant="body2" sx={{ color: 'text.secondary', py: 2 }}>
                    No document types registered.
                  </Typography>
                )}
              </Stack>
            )}

            {/* Tab 3: Regulatory Obligations */}
            {drawerTab === 3 && (
              <Stack spacing={1.5}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Statutory reporting clocks, IND/FDA correspondence deadlines, and safety review triggers.
                </Typography>
                {selectedProgram.obligationTypes && selectedProgram.obligationTypes.length > 0 ? (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 800, width: 150 }}>Obligation Key</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>Obligation Title</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>Regulatory Clock</TableCell>
                        <TableCell sx={{ fontWeight: 800, width: 110 }}>Deadline</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedProgram.obligationTypes.map((ob) => (
                        <TableRow key={ob.key} hover>
                          <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.775rem' }}>
                            {ob.key}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                              {ob.label}
                            </Typography>
                            {ob.basis && (
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {ob.basis}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.725rem' }}>
                            {ob.clockStart || 'USER_DEFINED'}
                          </TableCell>
                          <TableCell>
                            {ob.durationDays ? (
                              <Chip
                                size="small"
                                label={`${ob.durationDays} Days`}
                                sx={{
                                  fontWeight: 800,
                                  fontSize: '0.725rem',
                                  bgcolor: ob.durationDays <= 7 ? 'rgba(239, 68, 68, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                                  color: ob.durationDays <= 7 ? '#DC2626' : '#D97706',
                                }}
                              />
                            ) : (
                              'Variable'
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <Typography variant="body2" sx={{ color: 'text.secondary', py: 2 }}>
                    No obligation types defined.
                  </Typography>
                )}
              </Stack>
            )}

            {/* Tab 4: Pipeline Purposes */}
            {drawerTab === 4 && (
              <Stack spacing={1.5}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Pipeline blueprints bound to this program definition by purpose.
                </Typography>
                {selectedProgram.pipelinePurposes && selectedProgram.pipelinePurposes.length > 0 ? (
                  <Grid container spacing={1.5}>
                    {selectedProgram.pipelinePurposes.map((p) => (
                      <Grid size={{ xs: 12, sm: 6 }} key={p.key}>
                        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: '8px' }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <AccountTreeIcon sx={{ fontSize: 18, color: '#6366F1' }} />
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 750 }}>
                                {p.label}
                              </Typography>
                              <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                                {p.key}
                              </Typography>
                            </Box>
                          </Stack>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Typography variant="body2" sx={{ color: 'text.secondary', py: 2 }}>
                    No pipeline purposes registered.
                  </Typography>
                )}
              </Stack>
            )}

            {/* Tab 5: Setting Defs */}
            {drawerTab === 5 && (
              <Stack spacing={2}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Configuration fields requested when instantiating new programs under this definition.
                </Typography>
                {selectedProgram.settingDefs && selectedProgram.settingDefs.length > 0 ? (
                  selectedProgram.settingDefs.map((sec, idx) => (
                    <Paper key={idx} variant="outlined" sx={{ p: 1.5, borderRadius: '8px' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                        {sec.sectionName}
                      </Typography>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 800 }}>Field Name</TableCell>
                            <TableCell sx={{ fontWeight: 800 }}>Field Label</TableCell>
                            <TableCell sx={{ fontWeight: 800, width: 80 }}>Type</TableCell>
                            <TableCell sx={{ fontWeight: 800, width: 90 }}>Required</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {sec.items.map((it) => (
                            <TableRow key={it.name} hover>
                              <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.775rem' }}>{it.name}</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>{it.label}</TableCell>
                              <TableCell sx={{ textTransform: 'uppercase', fontSize: '0.725rem' }}>{it.type}</TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={it.required ? 'Required' : 'Optional'}
                                  sx={{
                                    fontSize: '0.7rem',
                                    fontWeight: 700,
                                    bgcolor: it.required ? 'rgba(239, 68, 68, 0.1)' : 'action.hover',
                                    color: it.required ? '#DC2626' : 'text.secondary',
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Paper>
                  ))
                ) : (
                  <Typography variant="body2" sx={{ color: 'text.secondary', py: 2 }}>
                    No setting definitions configured.
                  </Typography>
                )}
              </Stack>
            )}

            {/* Tab 6: Configuration JSON */}
            {drawerTab === 6 && (
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Stack direction="row" justifyContent="flex-end">
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={copiedJson ? <CheckIcon color="success" /> : <ContentCopyIcon sx={{ fontSize: 16 }} />}
                    onClick={copyProgramJson}
                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}
                  >
                    {copiedJson ? 'Copied' : 'Copy JSON'}
                  </Button>
                </Stack>
                <Box
                  sx={{
                    flex: 1,
                    maxHeight: 450,
                    overflowY: 'auto',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: '8px',
                    p: 1,
                  }}
                >
                  <JsonViewer json={selectedProgram} showLabel={false} maxHeight={450} />
                </Box>
              </Box>
            )}
          </Box>
        )}
      </DrawerPanel>

      {/* Create / Edit Program Definition Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px' } }}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
          {editingProgram ? `Edit Program Def: ${editingProgram.id}` : 'Create Program Definition'}
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            fullWidth
            size="small"
            label="Definition ID"
            value={formData.id || ''}
            onChange={(e) => setFormData({ ...formData, id: e.target.value })}
            placeholder="e.g. clinicalProgram"
            disabled={!!editingProgram}
            required
            helperText={editingProgram ? 'ID cannot be changed once created' : 'Unique identifier for the program definition'}
          />

          <TextField
            fullWidth
            size="small"
            label="Definition Name"
            value={formData.name || ''}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g. Clinical Program"
            required
          />

          <TextField
            fullWidth
            size="small"
            label="UI Label"
            value={formData.label || ''}
            onChange={(e) => setFormData({ ...formData, label: e.target.value })}
            placeholder="e.g. Clinical Program (IND/NDA)"
          />

          <FormControl fullWidth size="small">
            <InputLabel>Domain Workspace</InputLabel>
            <Select
              value={formData.domainId || 'biopharm'}
              label="Domain Workspace"
              onChange={(e) => setFormData({ ...formData, domainId: e.target.value })}
            >
              <MenuItem value="biopharm">BioPharm & Drug Discovery</MenuItem>
              <MenuItem value="general">General Platform</MenuItem>
              <MenuItem value="itsupport">IT Support & Ops</MenuItem>
              <MenuItem value="education">Education & Learning</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            size="small"
            label="Description"
            multiline
            rows={3}
            value={formData.description || ''}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Detailed description of the program definition, governance boundaries, and regulatory tracks..."
          />

          <TextField
            fullWidth
            size="small"
            label="Instance Icon"
            value={formData.instanceIcon || 'clinical'}
            onChange={(e) => setFormData({ ...formData, instanceIcon: e.target.value })}
            placeholder="e.g. clinical, science, business"
          />

          <FormControlLabel
            control={
              <Switch
                checked={formData.active !== false}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                color="secondary"
              />
            }
            label="Active Definition"
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleSaveProgram}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '8px' }}
          >
            {editingProgram ? 'Save Changes' : 'Create Definition'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProgramManagement;
